// whiteboard-store.js
import { makeObservable, observable, action, runInAction } from 'mobx';
import StoreUtils from './store-utils';
import UserApi from '../rest-api/user-api';
import DownloadUtils from '../utils/download-utils';

class WhiteboardStore {
    whiteboardData = {};
    db;
    idToken;
    imageStore;
    // needs similar search mechanism to fuseWrapper
    isInitialized = false;

    constructor(userId, idToken, imageStore) {
        this.db = StoreUtils.createDexieUserDB(userId, 'WhiteboardStore');
        this.idToken = idToken;
        this.imageStore = imageStore;

        // define your schema, e.g.
        this.db.version(1).stores({
            whiteboards:
                'whiteboardId, canvasImageLink, fileName, handoutId, diagramVariant, handoutContentName, handoutAlphabeticalName, handoutDocumentTitle, notes, whiteboardStatus, lastUpdated, updatedById, updatedByFirstName, updatedByLastName, patientId, patientName, patientSurname, patientEmail, clinicPatientId,  userId, handoutIdUserIdCompositeKey',
        });
        makeObservable(this, {
            whiteboardData: observable,
            setWhiteboardData: action,
            isInitialized: observable,
        });
    }

    async initialize(userId) {
        console.warn('WhiteboardStore initialize');
        const storedWhiteboards = await this.db.whiteboards.toArray();
        console.warn('Current DB Version:', this.db.verno);
        runInAction(() => {
            for (const wb of storedWhiteboards) {
                this.whiteboardData[wb.whiteboardId] = wb;
            }
        });

        try {
            // Attempt to fetch fresh from backend
            await this.fetchWhiteboardsFromBackend(userId);
        } catch (err) {
            console.error('WhiteboardStore initialization fetch error:', err);
        }
        runInAction(() => {
            this.isInitialized = true;
        });
    }

    async fetchWhiteboardsFromBackend(userId) {
        try {
            const result = await UserApi.getWhiteboardEntries(this.idToken);
            console.warn('RESULT in fetchWhiteboardsFromBackend: ', result);
            if (result && Array.isArray(result.whiteboards)) {
                await this.setWhiteboardData(result.whiteboards);
                await this.fetchAndCacheWhiteboardImages(result.whiteboards);
            }
        } catch (error) {
            console.error('Error fetching whiteboards from backend:', error);
        }
    }

    async fetchAndCacheWhiteboardImages(whiteboards) {
        const missingImages = [];

        for (const whiteboard of whiteboards) {
            if (!whiteboard.canvasImageLink) continue;

            // Check if image already exists in IndexedDB
            const existingImage = await this.imageStore.getImageFromIndexedDB(whiteboard.canvasImageLink);
            if (!existingImage) {
                missingImages.push(whiteboard.canvasImageLink);
            }
        }

        if (missingImages.length === 0) return; // No missing images

        console.warn(`Fetching ${missingImages.length} missing whiteboard images...`);

        try {
            const payload = {
                files: missingImages.map((key) => ({ method: 'get', key })),
                contentCategory: 'whiteboard',
            };

            // Request presigned URLs for missing images
            const resp = await UserApi.generatePresignedUrls(this.idToken, payload);
            const presignedUrls = resp.getUrls;

            const downloadPromises = presignedUrls.map(async ({ presignedUrl }, index) => {
                const whiteboardKey = missingImages[index];

                try {
                    // Download Image
                    const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);

                    // Save Image to IndexedDB
                    await this.imageStore.saveImageToIndexedDB(
                        whiteboardKey,
                        downloadResult.base64data,
                        downloadResult.eTag,
                    );

                    console.warn(`Successfully cached image: ${whiteboardKey}`);
                } catch (error) {
                    console.error(`Failed to download whiteboard image ${whiteboardKey}:`, error);
                }
            });

            await Promise.all(downloadPromises); // Wait for all downloads to complete
        } catch (error) {
            console.error('Error fetching presigned URLs for whiteboard images:', error);
        }
    }

    async setWhiteboardData(arrayOfWhiteboards) {
        if (!arrayOfWhiteboards || arrayOfWhiteboards.length === 0) return;
        // e.g. write to Dexie in bulk
        await this.db.whiteboards.bulkPut(arrayOfWhiteboards);
        runInAction(() => {
            arrayOfWhiteboards.forEach((wb) => {
                this.whiteboardData[wb.whiteboardId] = wb;
            });
        });
    }

    async getWhiteboardById(whiteboardId) {
        // First check in-memory
        if (this.whiteboardData[whiteboardId]) {
            return this.whiteboardData[whiteboardId];
        }

        // Then check IndexedDB
        return await this.db.whiteboards.get(whiteboardId);
    }

    async deleteDatabase() {
        await this.db.delete();
    }
}

export default async function createWhiteboardStore(userId, idToken, imageStore) {
    // console.log('=== createWhiteboardStore : ', userId, idToken);
    const store = new WhiteboardStore(userId, idToken, imageStore);
    await store.initialize(userId);
    return store;
}
